import axios from 'axios'
import { getStorageSync, setStorageSync } from './storage.js'
import { navigateTo } from './navigation.js'
import { showGlobalToast } from './toastService.js'
import {
  getSystemBaseURL,
  CHAT_BASE_URL,
} from '../common/js/constants.js'
import {
  ROUTE_LOGIN,
  BACKUP_API_PAGE,
} from '../common/js/routes.js'

// Create axios instance
const service = axios.create({
  timeout: 10000,
})

let unauthorizedHandled = false

// Request interceptor
service.interceptors.request.use(
  (config) => {
    // Get token from storage
    const token = getStorageSync('token') || ''
    
    // Determine server URL
    let serverUrl = getSystemBaseURL()
    
    if (config.chatURL) {
      serverUrl = getStorageSync('ownChatUrl') || CHAT_BASE_URL
    } else {
      serverUrl = getStorageSync('selectedLine') || getSystemBaseURL()
    }

    // Set base URL if not a third-party request
    if (!config.isThirdParty && serverUrl) {
      config.baseURL = serverUrl
    }

    // Set headers
    if (!config.headers) {
      config.headers = {}
    }
    
    if (config.chatURL) {
      config.headers['Content-Type'] = 'application/json'
      // Match uni-app: chat requests use plain token (not Bearer) with Partnerid
      config.headers['Authorization'] = token
      config.headers['Partnerid'] = '1'
    } else if (!config.isThirdParty) {
      config.headers['Content-Type'] = 'application/json'
      config.headers['Authorization'] = token
      config.headers['Partnerid'] = '1'
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
service.interceptors.response.use(
  (response) => {
    const res = response.data

    // Handle i18n message translation if needed
    if (res.msgId && window.__VUE_I18N__) {
      // This will be handled by the component using $t()
      // res.msgId = this.$t(res.msgId)
    }

    if (res.status === 200) {
      return res
    } else if (res.status === 411) {
      const errorMsg = res.msg || res.msgId
      return Promise.reject(new Error(errorMsg))
    } else if (res.status === 401 && !response.config.chatURL) {
      if (!unauthorizedHandled) {

        unauthorizedHandled = true
        setStorageSync('token', '')
        setStorageSync('riskNoticeSoundRun', '0')

        const message = window.__VUE_I18N__
          ? window.__VUE_I18N__.global.t('system.unauthorized')
          : 'system.unauthorized'

        showGlobalToast('error', message, () => {
          if (window.__VUE_ROUTER__) {
            const currentRoute = window.__VUE_ROUTER__.currentRoute.value
            if (currentRoute && currentRoute.path !== ROUTE_LOGIN) {
              window.__VUE_ROUTER__.push(ROUTE_LOGIN)
            }
          }
          unauthorizedHandled = false
        })

      }
      return Promise.reject(new Error(res.msgId || 'system.unauthorized'))
    } else if (res.status === 404) {
      return Promise.reject(new Error(res.msg || 'Not Found'))
    } else if (res.status === 501 || res.status === 500) {
      const errorMsg = res.msgId || (res.msg && res.msg !== 'Server Error' ? res.msg : 'system.exception')
      return Promise.reject(new Error(errorMsg))
    } else {
      if (response.status === 200) {
        return res
      } else if (!response.config.isThirdParty) {
        const errorMsg = res.msgId || res.msg || 'Request Error'
        return Promise.reject(new Error(errorMsg))
      } else {
        return res
      }
    }
  },
  (error) => {
    // Handle network errors
    if (error.response) {
      // Server responded with error status
      const responseData = error.response.data || {}
      const isServerError = error.response.status >= 500
      const responseMessage = responseData.msgId || responseData.msg
      const errorMsg = isServerError && (!responseMessage || responseMessage === 'Server Error')
        ? 'system.exception'
        : (responseMessage || error.message || 'Request Error')
      return Promise.reject(new Error(errorMsg))
    } else if (error.request) {
      // Request made but no response
      // Skip API line failover for Chat requests
      const isChatRequest = error.config && error.config.chatURL

      // If the request url contains 'qt.gtimg.cn', do not redirect to backup API page
      const requestUrl = error.config && error.config.url ? error.config.url : '';
      if (!isChatRequest && !(requestUrl && requestUrl.indexOf('qt.gtimg.cn') !== -1)) {
        // Navigate to backup API page after delay (if not already there)
        setTimeout(() => {
          if (window.__VUE_ROUTER__) {
            const currentRoute = window.__VUE_ROUTER__.currentRoute.value
            if (currentRoute && currentRoute.path !== BACKUP_API_PAGE) {
              const failedUrl = requestUrl || 'Unknown URL';
              const failReason = error.message || 'No specific error message';
              console.error(`API line failed: ${failedUrl}. Reason: ${failReason}`);
              window.__VUE_ROUTER__.push(BACKUP_API_PAGE)
            }
          }
        }, 1000)
      }
      
      return Promise.reject(error)
    } else {
      // Error setting up request
      return Promise.reject(error)
    }
  }
)

/**
 * HTTP request function (compatible with old uni.request interface)
 * @param {Object} options - Request options
 * @param {Function} errorHandler - Error handler callback
 * @param {Object} context - Vue component context (for i18n)
 * @returns {Promise}
 */
function httpRequest(options = {}, errorHandler, context) {
  const method = (options.method || 'GET').toUpperCase()
  const config = {
    method,
    url: options.url,
    data: options.data,
    params: method === 'GET' ? options.data : undefined,
  timeout: options.timeout || 10000,
  chatURL: options.chatURL,
  isThirdParty: options.isThirdParty,
  }

  return service.request(config)
    .then((res) => {
      return res
    })
    .catch((error) => {
      const errorMsg = error.message || error.msg || 'Network Error'
      if (errorHandler && typeof errorHandler === 'function') {
        errorHandler(errorMsg, error)
        return Promise.resolve({ data: {}, status: 'error', __failed: true })
      }
      return Promise.reject(error)
    })
}

export default httpRequest

